import React, { useEffect, useState } from 'react';
import { Monitor, Save, RefreshCw, BarChart2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SectionCard } from '../../components/ui/SectionCard';
import { Input, FormField } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import api from '../../services/api';

const DEFAULT_STATS = [
  { value: '+125', label: 'clients exportateurs' },
  { value: '8 200', label: 'shipments / an' },
  { value: '96%',   label: 'ponctualité 2025' },
];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user?.company?.type !== 'ADMIN') {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const [stats, setStats]     = useState(DEFAULT_STATS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState(null);

  function load() {
    setLoading(true);
    setError(null);
    api.get('/portal-settings/login-stats')
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setStats(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  function statField(i, key, val) {
    setSaved(false);
    setStats((prev) => prev.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await api.put('/portal-settings/login-stats', { stats });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.response?.data?.message || t('settings.save_error'));
    } finally {
      setSaving(false);
    }
  }

  if (user?.company?.type !== 'ADMIN') return null;

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('settings.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('settings.subtitle')}</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
        >
          <RefreshCw size={13} strokeWidth={1.75} className={loading ? 'animate-spin' : ''} />
          {t('common.refresh')}
        </button>
      </div>

      {/* Login stats section */}
      <SectionCard
        title={
          <span className="flex items-center gap-2">
            <BarChart2 size={14} strokeWidth={1.75} className="text-primary" />
            {t('settings.stats_section_title')}
          </span>
        }
        action={
          <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
            <Monitor size={12} strokeWidth={1.75} />
            {t('settings.stats_section_hint')}
          </span>
        }
      >
        <p className="text-[12px] text-slate-500 mb-5">
          {t('settings.stats_description')}
        </p>

        {/* Live preview */}
        <div className="bg-primary rounded-xl p-4 mb-5 flex gap-8">
          {stats.map((s, i) => (
            <div key={i}>
              <p className="text-white font-bold text-lg leading-none">
                {s.value || <span className="opacity-30">—</span>}
              </p>
              <p className="text-white/50 text-[11px] mt-1">
                {s.label || <span className="opacity-30">{t('settings.label_placeholder')}</span>}
              </p>
            </div>
          ))}
        </div>

        {/* Editable rows */}
        <div className="flex flex-col gap-4">
          {stats.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-4 items-end">
              <FormField label={t('settings.value_label', { n: i + 1 })}>
                <Input
                  value={s.value}
                  onChange={(e) => statField(i, 'value', e.target.value)}
                  placeholder="+125"
                />
              </FormField>
              <FormField label={t('settings.label_label')}>
                <Input
                  value={s.label}
                  onChange={(e) => statField(i, 'label', e.target.value)}
                  placeholder={t('settings.label_placeholder')}
                />
              </FormField>
            </div>
          ))}
        </div>

        {error && (
          <p className="mt-3 text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            loading={saving}
            iconRight={!saving ? Save : undefined}
            onClick={handleSave}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          {saved && (
            <span className="text-[12px] text-emerald-600 font-medium">
              {t('common.saved')}
            </span>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
